import React, { useEffect, useState } from 'react';
import {
	Control,
	Controller,
	useFieldArray,
	useFormContext,
} from 'react-hook-form';
import {
	Box,
	Button,
	TextField,
	Typography,
	IconButton,
	Divider,
	Switch,
	styled,
	Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
	RegulationFormValues,
	User,
	ResponsibilityType,
	Regulation,
} from '../../DataTable/dataTypes';
import ScheduleFields from './ScheduleFields';
import ResponsibilityFields from './ResponsibilityFields';
import TaskWorksheetsComponent from '../../TaskWorksheetsComponent/TaskWorksheetsComponent';
import TaskWorksheetsUpload from '../../TaskWorksheetsComponent/TaskWorksheetsUpload';
import {
	uploadTaskWorksheet,
	downloadTaskWorksheet,
	deleteTaskWorksheet,
} from '../../../api/tasksApi';
import LoadingModal from '../../modals/LoadingModal/LoadingModal';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { updateRegulation } from '../../../store/slices/regulationSlice';
import { Task } from '../../../types/interfaces';

interface TaskFieldsProps {
	control: Control<RegulationFormValues>;
	users: User[];
	types: ResponsibilityType[];
	disabled?: boolean;
	saveFormBefore: () => Promise<Regulation>;
}

const AntSwitch = styled(Switch)(({ theme }) => ({
	width: 28,
	height: 16,
	padding: 0,
	display: 'flex',
	'&:active': {
		'& .MuiSwitch-thumb': {
			width: 15,
		},
		'& .MuiSwitch-switchBase.Mui-checked': {
			transform: 'translateX(9px)',
		},
	},
	'& .MuiSwitch-switchBase': {
		padding: 2,
		'&.Mui-checked': {
			transform: 'translateX(12px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				opacity: 1,
				backgroundColor: '#19857b',
				...theme.applyStyles('dark', {
					backgroundColor: '#177ddc',
				}),
			},
		},
	},
	'& .MuiSwitch-thumb': {
		boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
		width: 12,
		height: 12,
		borderRadius: 6,
		transition: theme.transitions.create(['width'], {
			duration: 200,
		}),
	},
	'& .MuiSwitch-track': {
		borderRadius: 16 / 2,
		opacity: 1,
		backgroundColor: 'rgba(0,0,0,.25)',
		boxSizing: 'border-box',
		...theme.applyStyles('dark', {
			backgroundColor: 'rgba(255,255,255,.35)',
		}),
	},
}));

const TaskFields: React.FC<TaskFieldsProps> = ({
	control,
	users,
	types,
	disabled,
	saveFormBefore
}) => {
	const { fields, append, remove, update } = useFieldArray({
		control,
		name: 'tasks',
	});

	const { getValues, setValue } = useFormContext();
	const [selectedFile, setSelectedFile] = useState<(File | any)[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const dispatch = useDispatch<AppDispatch>();

	const [taskList, setTaskList] = useState<Task[]>([])

	useEffect(() => {
		const tasks = getValues(`tasks`);
		if (taskList.length === 0) setTaskList(tasks);
	}, [])

	const addTask = () => {
		append({
			description: '',
			preparationDays: 0,
			number: fields.length + 1,
			regulationId: 0,
			periodicity: 'monthly',
			taskUserResponsibilities: [],
			worksheets: [],
			confidential: false,
		});
	};


	const handleUploadWorksheet = async (taskIndex: number) => {
		try {			
			let taskId = getValues(`tasks.${taskIndex}.id`);
			if (!taskId) {
				const regulation = await saveFormBefore();
				const backendTask = regulation.tasks?.[taskIndex];

				if (!backendTask?.id) return;

				update(taskIndex, {
					...getValues(`tasks.${taskIndex}`),
					...backendTask,
				});

				taskId = backendTask.id;
			}

			setLoading(true);

			for (const item of selectedFile.filter(f => f.index === taskIndex)) {
				const worksheet = await uploadTaskWorksheet(item.file, taskId);

				if (worksheet) {
					const task = getValues(`tasks.${taskIndex}`);
					update(taskIndex, {
						...task,
						worksheets: [...(task.worksheets ?? []), worksheet],
					});
				}
			}

			setSelectedFile([]);
		} catch (e) {
			console.error('Error uploading worksheet:', e);
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadWorksheet = async (worksheetId: number) => {
		try {
			const { blob, fileName } = await downloadTaskWorksheet(worksheetId);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			link?.parentNode?.removeChild(link);
		} catch (error) {
			console.error('Error downloading worksheet:', error);
		}
	};



	const handleDeleteWorksheet = async (
		taskIndex: number,
		worksheetId: number
	) => {
		setLoading(true);
		try {
			await deleteTaskWorksheet(worksheetId);
			const worksheets = getValues(`tasks.${taskIndex}.worksheets`) || [];
			const updatedWorksheets = worksheets.filter(
				(ws: any) => ws.id !== worksheetId
			);
			setValue(`tasks.${taskIndex}.worksheets`, updatedWorksheets);
			const regulation = getValues() as Regulation;
			dispatch(updateRegulation(regulation));
		} catch (error) {
			console.error('Error deleting worksheet:', error);
		} finally {
			setLoading(false);
		}
	};

	const addFileSelected = (file: any, index: any) => {
		setSelectedFile([...selectedFile, { file, index }])
	}

	const removeFileSelected = (obj: any) => {
		try {
			const indexDelete = selectedFile.findIndex(x => x.file.name === obj?.file?.name && x.index === obj?.index)
			const selected = [...selectedFile]
			selected.splice(indexDelete, 1);
			setSelectedFile(selected)
		} catch (error) {
			console.error('Error deleting file:', error);
		}
	}

	const inputFile = (index: any) => <React.Fragment><input
		type="file"
		onChange={(e) => addFileSelected(e.target.files?.[0] || null, index)}
		disabled={disabled} // Deshabilitar en modo solo lectura
	/>
		<br /></React.Fragment>

	return (
		<>
			<LoadingModal open={loading} />
			<h3>Tareas</h3>
			{fields.map((field, index) => {
				return <Box
					key={field.id}
					sx={{ border: '1px solid lightgray', position: 'relative' }}
					mb={'20px'}
					p={3}
				>
					<Typography fontSize={'110%'} mb={2} fontWeight={'bold'}>
						Tarea {index + 1}
					</Typography>

					<Stack direction="row" spacing={1}
						sx={{ alignItems: 'center', position: 'absolute', top: '26px', right: '80px' }}>
						<Typography>Confidencial</Typography>

						<Controller
							name={`tasks.${index}.confidential`}
							control={control}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<AntSwitch
									checked={value}
									onChange={onChange}
									disabled={disabled}
								/>
							)}
						/>
					</Stack>

					{!disabled && (
						<IconButton
							onClick={() => remove(index)}
							sx={{ position: 'absolute', top: '10px', right: '10px' }}
						>
							<CloseIcon />
						</IconButton>
					)}
					<Box display={'flex'} flexDirection={'column'} gap={'10px'}>
						<Controller
							name={`tasks.${index}.description`}
							control={control}
							rules={{ required: 'Descripción de la tarea es obligatorio' }}
							render={({ field, fieldState: { error } }) => (
								<TextField
									{...field}
									label="Descripción de la Tarea"
									error={!!error}
									helperText={error ? error.message : null}
									fullWidth
									disabled={disabled} // Deshabilitar en modo solo lectura
								/>
							)}
						/>
						<Typography>Calendario</Typography>
						<ScheduleFields
							control={control}
							taskIndex={index}
							disabled={disabled}
							firstDueDate={getValues(`tasks.${index}.firstDueDate`)}
						/>
						<Typography>Responsables de la Tarea</Typography>
						<Divider />
						<Box>
							<Box display={'flex'} flexWrap={'wrap'} gap={'10px'}>
								<Controller
									name={`tasks.${index}.taskUserResponsibilities`}
									control={control}
									render={({ field }) => (
										<>
											<ResponsibilityFields
												control={control}
												taskIndex={index}
												users={users}
												types={types}
												disabled={disabled}
												userResponsabilityLst={getValues(`tasks.${index}.taskUserResponsibilities`) ?? []}
											/>
										</>
									)}
								/>
							</Box>
						</Box>
						{field.id && !disabled && (
							<>
								<TaskWorksheetsComponent
									worksheets={getValues(`tasks.${index}.worksheets`)}
									onDownload={handleDownloadWorksheet}
									onDelete={(worksheetId: number) =>
										handleDeleteWorksheet(index, worksheetId)
									}
								/>
								<TaskWorksheetsUpload
									selectedFile={selectedFile}
									onDelete={removeFileSelected}
									index={index}
								/>
								<Box>
									{inputFile(index)}
									<br />
									<Button
										variant="contained"
										color="primary"
										onClick={() => handleUploadWorksheet(index)}
										disabled={selectedFile.length === 0 || disabled} // Deshabilitar si no hay archivo o si está en modo solo lectura
										sx={{ padding: '5px', fontSize: '12px' }}
									>
										Guardar Template
									</Button>

								</Box>
							</>
						)}
					</Box>
				</Box>
			})}
			{!disabled && (
				<Button variant="contained" color="primary" onClick={addTask}>
					Añadir Tarea
				</Button>
			)}
		</>
	);
};

export default TaskFields;
